import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useMemo } from "react";
import { z } from "zod";
import { components as c } from "@/components";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { effectiveReferenceDateMode, type ReferenceDateModeT } from "@/features/templates/utils/referenceDateMode";
import { useAuth } from "@/utils/auth";
import { templateSettingsSchema, TrackingType, type TemplateSettingsSchemaT } from "@patient-watch/types";
import {
    Questionnaire,
    QuestionnaireChoice,
    QuestionnaireChoiceDescription,
    QuestionnaireChoices,
    QuestionnaireItem,
} from "@patient-watch/ui/questionnaire";

function stopSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
}

/**
 * Step 3 of every template, straight after the intervention: which date follow-ups count from.
 * The diary start date (questionnaires start at once), or blank for the patient to give in setup.
 */
const TemplateDateStep = () => {
    const auth = useAuth();
    const copy = useTemplatesCopy();
    const navigate = useNavigate();
    const { templateId } = Route.useParams();
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const {
        currentStep,
        setCurrentStep,
        markStepComplete,
        nextStep,
        updateEventSteps,
        getNextStepRoute,
        getPreviousStepRoute,
        steps,
    } = useTemplateNavigationStore();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search as Partial<TemplateSettingsSchemaT>
    );

    const mode = effectiveReferenceDateMode(search.reference_date_mode);
    const effectiveEvents = useMemo(
        () => (search.required_events ?? []).filter((event) => event !== "skip") as TrackingType[],
        [search.required_events]
    );

    // A reload lands here with only the base steps in the store, so rebuild the intervention steps that follow.
    useEffect(() => {
        updateEventSteps(effectiveEvents);
        setCurrentStep("date");
    }, [effectiveEvents, updateEventSteps, setCurrentStep]);

    const isLastStep = steps.findIndex((step) => step.key === "date") === steps.length - 1;

    const go = (to: "next" | "back", next: Partial<TemplateSettingsSchemaT>) => {
        const route =
            to === "next"
                ? getNextStepRoute(currentStep, { templateId })
                : getPreviousStepRoute(currentStep, { templateId });
        if (to === "next" && !route) {
            finalize(next);
            return;
        }
        if (!route) return;
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", next);
        if (!payload) return;
        if (to === "next") {
            markStepComplete("date");
            nextStep();
        }
        navigate({ ...route, search: payload as never });
    };

    const choose = (next: ReferenceDateModeT) => {
        const payload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", {
            ...search,
            reference_date_mode: next,
        });
        if (!payload) return;
        navigate({
            to: "/templates/$templateId/date",
            params: { templateId },
            search: payload as never,
            replace: true,
        });
    };

    const options: { value: ReferenceDateModeT; label: string; hint: string }[] = [
        { value: "diary_start", label: copy.dateDiaryStart, hint: copy.dateDiaryStartHint },
        { value: "ask_patient", label: copy.dateAskPatient, hint: copy.dateAskPatientHint },
    ];

    const settings = { ...search, reference_date_mode: mode } as Partial<TemplateSettingsSchemaT>;

    return (
        <div className="space-y-8">
            <c.ui.Card>
                <c.ui.CardHeader>
                    <c.ui.CardTitle>{copy.dateQuestion}</c.ui.CardTitle>
                </c.ui.CardHeader>
                <c.ui.CardContent>
                    <Questionnaire onSubmit={stopSubmit} onReset={stopSubmit}>
                        <QuestionnaireItem name="reference_date_mode" aria-label={copy.dateQuestion}>
                            <QuestionnaireChoices>
                                {options.map((option) => (
                                    <QuestionnaireChoice
                                        key={option.value}
                                        value={option.value}
                                        checked={mode === option.value}
                                        onChange={() => choose(option.value)}
                                        className="items-start"
                                        inputProps={{ "data-testid": `reference-date-mode-${option.value}` } as never}
                                    >
                                        <span className="flex flex-col gap-0.5">
                                            <span className="font-medium">{option.label}</span>
                                            <QuestionnaireChoiceDescription>
                                                {option.hint}
                                            </QuestionnaireChoiceDescription>
                                        </span>
                                    </QuestionnaireChoice>
                                ))}
                            </QuestionnaireChoices>
                        </QuestionnaireItem>
                    </Questionnaire>
                </c.ui.CardContent>
            </c.ui.Card>

            <TemplateStepFooter
                onPrimaryClick={() => go("next", settings)}
                onBack={() => go("back", settings)}
                primaryAction={isLastStep ? "save" : "next"}
                primaryLabel={isLastStep ? copy.saveTemplate : undefined}
                isLoadingPrimary={isLastStep ? isSubmitting : undefined}
                disabledPrimary={isLastStep ? isTemplateOwnerLookupBlockingSubmit : undefined}
            />
        </div>
    );
};

const dateSearchSchema = templateSettingsSchema.partial().extend({
    required_events: z.array(z.union([TrackingType, z.literal("skip")])).optional(),
});

export const Route = createFileRoute("/_app/templates/$templateId/date")({
    component: TemplateDateStep,
    validateSearch: (search) => dateSearchSchema.parse(search),
});
